import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PublicNav from '../components/PublicNav';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const BLUE = '#2563eb';
const BLUE_DARK = '#1d4ed8';
const GRADIENT = 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)';
const LIGHT_GRAY = '#fafbfc';
const VISUAL_BG = '#e0f2fe';
const WHITE = '#ffffff';
const TEXT = '#0a0a0a';
const TEXT_SECONDARY = '#555555';
const TEXT_MUTED = '#666666';
const BORDER = '#e5e7eb';
const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

function FadeIn({ children, delay = 0, isMobile, style }) {
  const offset = isMobile ? 10 : 20;
  const duration = isMobile ? 0.4 : 0.6;
  return (
    <motion.div
      initial={{ opacity: 0, y: offset }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration, ease: 'easeOut', delay }}
      style={style}
    >
      {children}
    </motion.div>
  );
}

function VisualPlaceholder({ label }) {
  return (
    <div style={{ background: VISUAL_BG, height: 400, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 14, color: TEXT_MUTED, fontWeight: 500 }}>{label}</div>
    </div>
  );
}

function HoverButton({ to, children }) {
  const [hover, setHover] = useState(false);
  return (
    <Link
      to={to}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: WHITE,
        color: BLUE,
        fontSize: 13,
        fontWeight: 700,
        padding: '13px 32px',
        borderRadius: 7,
        textDecoration: 'none',
        display: 'inline-block',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        transform: hover ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hover ? '0 12px 32px rgba(0,0,0,0.2)' : 'none',
      }}
    >
      {children}
    </Link>
  );
}

function ValueCard({ title, body, delay, isMobile }) {
  const [hover, setHover] = useState(false);
  return (
    <FadeIn isMobile={isMobile} delay={delay}>
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          padding: '40px 32px',
          background: LIGHT_GRAY,
          border: '1px solid ' + (hover ? BLUE : BORDER),
          borderRadius: 10,
          transition: 'border-color 0.2s ease, transform 0.2s ease',
          transform: hover ? 'translateY(-4px)' : 'translateY(0)',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 700, color: TEXT, marginBottom: 12 }}>{title}</div>
        <div style={{ fontSize: 14, fontWeight: 400, color: TEXT_MUTED, lineHeight: 1.6 }}>{body}</div>
      </div>
    </FadeIn>
  );
}

const VALUES = [
  { title: 'Clarity first', body: 'No guessing. No Reddit. No outdated data. Just honest answers about where you stand and where you belong.' },
  { title: 'Our methodology', body: 'We scan official school data and built our own accuracy framework. Not AAMC aggregates that lag cycles behind. Our own system, our own standards.' },
  { title: 'For everyone', body: "Whether you're a 505 MCAT or a 528. Whether you're deciding between DO and MD. Your stats matter. Your story matters more." },
];

export default function About() {
  const isMobile = useIsMobile();
  const pxDesktop = 64;
  const pxMobile = 24;

  return (
    <div style={{ background: WHITE, minHeight: '100vh', fontFamily: FONT }}>
      <PublicNav isMobile={isMobile} active="about" />

      {/* Hero */}
      <div style={{ background: GRADIENT, padding: isMobile ? `80px ${pxMobile}px` : `140px ${pxDesktop}px`, textAlign: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          style={{ maxWidth: 760, margin: '0 auto' }}
        >
          <h1 style={{ fontSize: isMobile ? 34 : 56, fontWeight: 800, color: TEXT, letterSpacing: '-1.5px', lineHeight: 1.1, margin: 0, marginBottom: 20 }}>
            The information existed. It was just everywhere.
          </h1>
          <p style={{ fontSize: 18, fontWeight: 400, color: TEXT_SECONDARY, lineHeight: 1.6, margin: 0 }}>
            In 2024, I watched someone I love spend entire weekends searching Reddit, YouTube, and forums just trying to figure out where they stood. That&rsquo;s when I knew something had to change.
          </p>
        </motion.div>
      </div>

      {/* Story 1: What I saw */}
      <div style={{ background: GRADIENT, padding: isMobile ? `64px ${pxMobile}px` : `120px ${pxDesktop}px` }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 32 : 80, alignItems: 'center' }}>
          <FadeIn isMobile={isMobile}>
            <h2 style={{ fontSize: isMobile ? 36 : 48, fontWeight: 800, color: TEXT, letterSpacing: '-1px', margin: 0, marginBottom: 24 }}>What I saw</h2>
            <p style={{ fontSize: 16, fontWeight: 400, color: TEXT_SECONDARY, lineHeight: 1.75, marginBottom: 20 }}>
              My wife was applying to medical school. Hours scrolling. Late nights rewriting. Uncertainty about whether any of it mattered.
            </p>
            <p style={{ fontSize: 16, fontWeight: 400, color: TEXT_SECONDARY, lineHeight: 1.75, marginBottom: 20 }}>
              She had the data scattered across dozens of sources. She had opinions from people who didn&rsquo;t know her situation. She had statistics from websites that hadn&rsquo;t updated in years.
            </p>
            <p style={{ fontSize: 16, fontWeight: 400, color: TEXT_SECONDARY, lineHeight: 1.75, margin: 0 }}>
              What she didn&rsquo;t have was clarity. Not from day one. Not even by day 365.
            </p>
          </FadeIn>
          <FadeIn isMobile={isMobile} delay={0.15}>
            <VisualPlaceholder label="[Visual: chaos of Reddit, YouTube, forums, scattered papers]" />
          </FadeIn>
        </div>
      </div>

      {/* Story 2: The problem */}
      <div style={{ background: LIGHT_GRAY, padding: isMobile ? `64px ${pxMobile}px` : `120px ${pxDesktop}px` }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 32 : 80, alignItems: 'center' }}>
          <FadeIn isMobile={isMobile} style={{ order: isMobile ? 2 : 1 }}>
            <VisualPlaceholder label="[Visual: data structure, connected nodes, information flowing]" />
          </FadeIn>
          <FadeIn isMobile={isMobile} delay={0.15} style={{ order: isMobile ? 1 : 2 }}>
            <h2 style={{ fontSize: isMobile ? 36 : 48, fontWeight: 800, color: TEXT, letterSpacing: '-1px', margin: 0, marginBottom: 24 }}>The problem</h2>
            <p style={{ fontSize: 16, fontWeight: 400, color: TEXT_SECONDARY, lineHeight: 1.75, marginBottom: 20 }}>
              The data exists. Schools publish it. The patterns are there. But no one had built a system to analyze it consistently and tell you what it actually means.
            </p>
            <p style={{ fontSize: 16, fontWeight: 400, color: TEXT_SECONDARY, lineHeight: 1.75, marginBottom: 20 }}>
              But no one had built their own system. No tool that analyzes real school data and applies a consistent methodology to tell you honestly where you stand &mdash; at every school, across 150+ programs. So we built one.
            </p>
            <p style={{ fontSize: 16, fontWeight: 400, color: TEXT_SECONDARY, lineHeight: 1.75, margin: 0 }}>
              Information without clarity is just noise. And every pre-med was drowning in it.
            </p>
          </FadeIn>
        </div>
      </div>

      {/* Story 3: Why I built this */}
      <div style={{ background: GRADIENT, padding: isMobile ? `64px ${pxMobile}px` : `120px ${pxDesktop}px` }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 32 : 80, alignItems: 'center' }}>
          <FadeIn isMobile={isMobile}>
            <h2 style={{ fontSize: isMobile ? 36 : 48, fontWeight: 800, color: TEXT, letterSpacing: '-1px', margin: 0, marginBottom: 24 }}>Why I built this</h2>
            <p style={{ fontSize: 16, fontWeight: 400, color: TEXT_SECONDARY, lineHeight: 1.75, marginBottom: 20 }}>
              I&rsquo;m a finance person. I think in data. I look at problems and ask: is this solvable? This one was.
            </p>
            <p style={{ fontSize: 16, fontWeight: 400, color: TEXT_SECONDARY, lineHeight: 1.75, marginBottom: 20 }}>
              When she got her first acceptance &mdash; after all the late nights, the rewrites, the uncertainty &mdash; I knew exactly what I wanted to build. A tool that gives every pre-med that same clarity from day one.
            </p>
            <p style={{ fontSize: 16, fontWeight: 400, color: TEXT_SECONDARY, lineHeight: 1.75, margin: 0 }}>
              Not guesses. Not Reddit threads. Just honest, data-driven answers about where you actually stand.
            </p>
          </FadeIn>
          <FadeIn isMobile={isMobile} delay={0.15}>
            <VisualPlaceholder label="[Visual: acceptance letter, clarity moment, breakthrough]" />
          </FadeIn>
        </div>
      </div>

      {/* Values */}
      <div style={{ background: WHITE, padding: isMobile ? `64px ${pxMobile}px` : `100px ${pxDesktop}px` }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <FadeIn isMobile={isMobile} style={{ textAlign: 'center', marginBottom: isMobile ? 48 : 72 }}>
            <h2 style={{ fontSize: isMobile ? 36 : 48, fontWeight: 800, color: TEXT, letterSpacing: '-1px', margin: 0, marginBottom: 14 }}>What PreVisr is</h2>
            <p style={{ fontSize: 16, fontWeight: 400, color: TEXT_MUTED, margin: 0 }}>Three principles we build on.</p>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? 24 : 32 }}>
            {VALUES.map(function (v, i) {
              return <ValueCard key={v.title} title={v.title} body={v.body} delay={i * 0.1} isMobile={isMobile} />;
            })}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div style={{ background: `linear-gradient(135deg, ${BLUE} 0%, ${BLUE_DARK} 100%)`, color: WHITE, padding: isMobile ? `56px ${pxMobile}px` : `80px ${pxDesktop}px`, textAlign: 'center' }}>
        <FadeIn isMobile={isMobile}>
          <h2 style={{ fontSize: isMobile ? 32 : 44, fontWeight: 800, margin: 0, marginBottom: 16 }}>Ready to know your odds?</h2>
          <p style={{ fontSize: 16, color: '#bfdbfe', margin: 0, marginBottom: 32 }}>Build your school list in minutes. Free forever. No credit card needed.</p>
          <HoverButton to="/signup">Get started free</HoverButton>
        </FadeIn>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid #f0f0f0', padding: isMobile ? `32px ${pxMobile}px` : `48px ${pxDesktop}px`, textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: '#999999', marginBottom: 12 }}>PreVisr &mdash; Medical school matching for pre-meds</div>
        <div style={{ display: 'flex', gap: 20, justifyContent: 'center' }}>
          <Link to="/privacy" style={{ fontSize: 12, color: '#999999', textDecoration: 'none' }}>Privacy</Link>
          <Link to="/terms" style={{ fontSize: 12, color: '#999999', textDecoration: 'none' }}>Terms</Link>
        </div>
      </div>

    </div>
  );
}
